import { TextButton } from '@creditornot/cb-button';
import { CaretDown } from '@creditornot/cb-icons';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

type Props = {
  allowEmpty: boolean;
  languages: Language[];
  active: Language | null;
  onChange: (code: Language | null) => void;
  defaultLabel?: string;
};

const languageKeys: { [key in Language]: string } = {
  ar: 'languages.ar',
  'ar-IL': 'languages.ar-IL',
  az: 'languages.az',
  cs: 'languages.cs',
  da: 'languages.da',
  de: 'languages.de',
  ee: 'languages.ee',
  el: 'languages.el',
  en: 'languages.en',
  et: 'languages.et',
  fi: 'languages.fi',
  fr: 'languages.fr',
  he: 'languages.he',
  hr: 'languages.hr',
  hu: 'languages.hu',
  is: 'languages.is',
  ja: 'languages.ja',
  ka: 'languages.ka',
  lt: 'languages.lt',
  lv: 'languages.lv',
  nb: 'languages.nb',
  pl: 'languages.pl',
  ru: 'languages.ru',
  sk: 'languages.sk',
  sl: 'languages.sl',
  sq: 'languages.sq',
  sr: 'languages.sr',
  sv: 'languages.sv',
  uz: 'languages.uz',
};

type LanguageListItem = {
  active: boolean;
  onClick: () => void;
  closeOnClick: boolean;
  title: string;
};

const StyledPopover = styled(Popover).attrs({
  disableContentPadding: true,
  hideWhenOutOfView: false,
  placement: 'bottom',
  renderArrow: false,
  showOnHover: false,
  closeOnClickInside: true,
})`
  width: 116px;
`;

const Trigger = styled(TextButton).attrs({
  variant: 'blue',
  size: 'small',
  icon: <CaretDown />,
  iconPosition: 'right',
})`
  width: 100%;
  justify-content: flex-end;
`;

const DropdownRoot = styled.div`
  max-height: 400px;
  width: 130px;
  overflow-y: scroll;
  padding: 8px;
`;

const DropdownItem = styled(MenuActionItem).attrs({ variant: 'black' })`
  & + & {
    margin-block-start: 8px;
  }
`;

const LanguagePicker = ({ allowEmpty, languages, active, onChange, defaultLabel }: Props) => {
  const empty = allowEmpty
    ? [{ active: false, title: 'language-picker.none', onClick: () => onChange(null), closeOnClick: true }]
    : [];

  const items = empty.concat(
    languages.map(lang => ({
      active: active === lang,
      onClick: () => onChange(lang),
      closeOnClick: true,
      title: languageKeys[lang],
    })),
  );

  const handleItemClick = (item: LanguageListItem) => {
    item.onClick && item.onClick();
  };

  return (
    <StyledPopover
      content={() => (
        <DropdownRoot>
          {items &&
            items.map(item => (
              <DropdownItem key={item.title} selected={item.active} onClick={() => handleItemClick(item)}>
                <FormattedMessage id={item.title} />
              </DropdownItem>
            ))}
        </DropdownRoot>
      )}
    >
      <Trigger>
        <FormattedMessage id={(active && languageKeys[active]) || defaultLabel || 'language-picker.none'} />
      </Trigger>
    </StyledPopover>
  );
};

export default LanguagePicker;
